import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventLogService } from '../services/eventLogService';
import { useToastContext } from '../contexts/ToastContext';

export const useEventLogs = (bookingId) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  // Fetch event logs
  const { data, isLoading, error } = useQuery({
    queryKey: ['eventLogs', bookingId],
    queryFn: () => eventLogService.getEventLogs(bookingId),
    enabled: !!bookingId,
    select: (response) => response?.data || [],
  });

  // Start event mutation (no onSuccess/onError here - handled in wrapper)
  const startEventMutation = useMutation({
    mutationFn: (data) => eventLogService.startEvent(bookingId, data),
  });

  // End event mutation (no onSuccess/onError here - handled in wrapper)
  const endEventMutation = useMutation({
    mutationFn: (data) => eventLogService.endEvent(bookingId, data),
  });

  return {
    eventLogs: data || [],
    isLoading,
    error,
    startEvent: (data, options = {}) => {
      const customOnSuccess = options?.onSuccess;
      const customOnError = options?.onError;
      
      startEventMutation.mutate(data, {
        onSuccess: (response) => {
          queryClient.invalidateQueries(['eventLogs', bookingId]);
          queryClient.invalidateQueries(['vendorBooking', bookingId]);
          queryClient.invalidateQueries(['booking', bookingId]);
          showToast('Event started successfully', 'success');
          // Call the custom onSuccess callback if provided
          if (customOnSuccess) {
            customOnSuccess(response);
          }
        },
        onError: (error) => {
          showToast(
            error?.response?.data?.message || 'Failed to start event',
            'error'
          );
          // Call the custom onError callback if provided
          if (customOnError) {
            customOnError(error);
          }
        },
      });
    },
    endEvent: (data, options = {}) => {
      const customOnSuccess = options?.onSuccess;
      const customOnError = options?.onError;
      
      endEventMutation.mutate(data, {
        onSuccess: (response) => {
          // Invalidate queries first
          queryClient.invalidateQueries(['eventLogs', bookingId]);
          queryClient.invalidateQueries(['vendorBooking', bookingId]);
          queryClient.invalidateQueries(['booking', bookingId]);
          
          // Show toast
          showToast('Event ended successfully. Booking status updated to completed.', 'success');
          
          // Call the custom onSuccess callback if provided
          if (customOnSuccess) {
            customOnSuccess(response);
          }
        },
        onError: (error) => {
          showToast(
            error?.response?.data?.message || 'Failed to end event',
            'error'
          );
          // Call the custom onError callback if provided
          if (customOnError) {
            customOnError(error);
          }
        },
      });
    },
    isStarting: startEventMutation.isPending,
    isEnding: endEventMutation.isPending,
  };
};

